/**
 * 이상한 방 - 매일의 방(날짜 키 산술 · 시드 · 연속 출석 · 그날의 기록)
 *
 * 근거 문서: 로드맵 확정안 4절 6번(매일의 방 · 연속 출석 - 차용 요소) · 3.1절(핀 표의 `dailyFinish`·`streakDay`) ·
 *           `이상한방-컨셉시트-v0.1.md` 4.5절(점수) · `이상한방-상태모델-v0.1.md` 9.5절(싱글의 포지션) ·
 *           `core/rng.ts` 머리말("같은 시드는 같은 방을 만들어야 한다 - 일일 챌린지의 전제")
 *
 * ## 이 파일은 시계를 모른다
 *
 * 날짜는 `app/dateKey.ts`가 문자열(`YYYY-MM-DD`)로 굳혀 **인자로** 넘긴다. 여기에 `Date`가 한 줄이라도
 * 들어오면 "어제였는가"를 두 층이 각자 판단하게 되고, 그중 하나가 조용히 관대해진다.
 * `daily.test.ts`가 소스에서 `Date`가 없음을 검사한다.
 *
 * ## 날짜 산술을 손으로 짜는 이유
 *
 * "하루 뒤인가"를 알려면 달력이 필요한데 `Date`를 못 쓴다. 그래서 그레고리력을 정수 산술로 편다
 * (`daysFromCivil` - Howard Hinnant의 civil_from_days 계열 공식). 부동소수도 시간대도 없고, 같은 입력이
 * 어느 기기에서나 같은 정수를 낸다. 윤년·달 경계·해 경계가 공식 안에 들어 있어 표를 따로 두지 않는다.
 *
 * ## 되돌린 벽시계는 늘리지도 끊지도 않는다
 *
 * 날짜 키는 문자열이고 비교도 문자열이라, 시각을 어제로 돌리면 **어제의 키**가 그대로 나온다.
 * 같은 날 두 번은 증가가 없고(`lastDateKey === dateKey`), 지나간 날은 **출석을 움직이지 않는다**
 * (기록은 남는다 - `finishDaily`의 주석). 끊지도 않는 이유는 시간대를 넘나드는 사람(비행기·해외 출장)이
 * 진짜로 있기 때문이다 - 되돌린 하루로 연속이 0이 되면 그건 부정 방지가 아니라 정직한 사람에 대한 벌칙이다.
 *
 * ## 출석은 "앱을 열었다"가 아니라 "매일의 방을 끝냈다"이다
 *
 * 앱을 여는 것만으로 핀이 붙으면 게임을 하지 않아도 보상이 쌓이고, 그러면 매일의 방이 아니라
 * 매일의 버튼이 된다. 그래서 출석 갱신과 그날의 기록은 **한 함수**에서 함께 일어난다(`finishDaily`).
 *
 * ## 재도전은 연습이다 - 첫 결과만 기록한다
 *
 * 매일의 방은 같은 시드를 견주는 곳이라 "몇 번째 판의 점수인가"가 섞이면 비교가 성립하지 않는다.
 * 그래서 그날의 첫 결과만 남고 두 번째부터는 상태가 **참조 동일**로 돌아온다(호출자는 그것으로 "연습이었다"를 안다).
 * 같은 이유로 `attempt`는 `DAILY_ATTEMPT`(0)에 고정이다 - 재도전마다 변화가 달라지면(`gameState.ts`의 시드 분리)
 * 같은 날 사람마다 다른 이상한 곳을 찾게 된다.
 */

import { STREAK_DAYS_CAP } from './economy/pins'

/* --- 상수 ------------------------------------------------------------------- */

/** 날짜 키의 모양. 값의 유효성(2월 30일 같은 것)은 `isDateKey`가 달력으로 한 번 더 본다. */
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * 매일의 방의 시도 번호. **고정 0이다.**
 * `gameState.changeSeedFor`가 `${seed}:a${attempt}`로 변화를 가르므로, 이 값이 흔들리면
 * 같은 날짜 키를 받은 두 사람이 다른 이상한 곳을 찾게 된다(로드맵 M9 규칙 1).
 */
export const DAILY_ATTEMPT = 0

/** 시드 접두사. 단계 시드(`level-N`)·자유 모드 시드와 절대 겹치지 않게 이름 공간을 하나 판다. */
const DAILY_SEED_PREFIX = 'daily:'

/** 연속 출석의 시작값. 하루 건너뛴 사람도 오늘 왔으면 **1일째**이지 0일째가 아니다. */
export const STREAK_START = 1

/**
 * 연속 출석의 상한. 핀 표의 `min(n, 7)`(3.1절)과 **같은 수 하나**를 본다 -
 * 저장된 값이 7에서 멈추므로 화면과 핀이 다른 수를 말할 길이 없다.
 */
export const STREAK_CAP = STREAK_DAYS_CAP

/**
 * 남겨 두는 그날의 기록 수. 진행 저장은 **한 덩어리**라(`progress.ts` 머리말) 이 표가 무한히 자라면
 * 언젠가 용량 초과 한 번이 단계 기록까지 통째로 잃게 한다. 60일이면 두 달 치 달력이고, 그보다 앞의 날짜를
 * 화면에서 되짚는 길은 없다.
 */
export const DAILY_RECORD_KEEP = 60

/* --- 타입 ------------------------------------------------------------------- */

/**
 * 그날 한 판의 기록. 단계 기록(`progress.LevelRecord`)과 **다른 표**에 산다(로드맵 M9 규칙 2) -
 * 매일의 방은 단계가 아니라 날짜로 세는 것이고, 섞으면 "1단계 최고 기록"이 어느 날의 것인지 알 수 없게 된다.
 */
export type DailyRecord = Readonly<{
  score: number
  foundCount: number
  /** 그날의 이상한 곳 수. 모르면 `null`("안 적었다")이지 0이 아니다(`progress.ts`와 같은 규약). */
  changeCount: number | null
  /** 프레임 누적 경과(ms). 시계에서 오지 않는다. */
  elapsedMs: number | null
}>

/** 한 판의 결과 중 매일의 방이 보는 것. `RoundOutcome`(접착층)에서 뽑아 넘긴다. */
export type DailyOutcome = Readonly<{
  score: number
  foundCount: number
  changeCount?: number
  elapsedMs?: number
}>

/** 저장되는 매일의 방 상태. `progress.Progress.daily`가 이 모양이다. */
export type DailyState = Readonly<{
  /** 마지막으로 완주한 날. 아직 없으면 `null`. */
  lastDateKey: string | null
  /** 연속 출석 일수(1~`STREAK_CAP`). 아직 없으면 0이다. */
  streak: number
  /** 날짜 키 → 그날의 첫 결과. */
  records: Readonly<Record<string, DailyRecord>>
}>

/** `finishDaily`의 결과. `first`가 거짓이면 그날 것은 이미 있었고(연습) 상태는 참조 동일이다. */
export type DailyFinish = Readonly<{
  state: DailyState
  /** 이 판 뒤의 연속 출석 일수. 핀 산식(`economy/pins.pinsForDaily`)이 받는 값이다. */
  streak: number
  /** 그날의 첫 기록이었는가. 저장도 완주 핀도 이때만 움직인다. */
  first: boolean
  /**
   * 이 판이 **출석으로 셌는가**. 지나간 날의 판은 기록만 남고 출석은 전진하지 않으므로 거짓이다
   * (머리말의 "지나간 날"). 연속 핀은 이때만 붙는다 - 참이면 `streak`이 방금 오른 값이고,
   * 거짓이면 예전 값이라 그것으로 핀을 주면 지나간 날짜를 훑는 것만으로 연속 핀이 반복된다.
   */
  attended: boolean
}>

/* --- 날짜 산술 (정수만) --------------------------------------------------------- */

const DAYS_IN_MONTH: readonly number[] = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return DAYS_IN_MONTH[month - 1] ?? 0
}

/**
 * 그레고리력 → 1970-01-01부터의 일수. 시간대도 시각도 없는 **순수 정수 산술**이다.
 * (Hinnant의 days_from_civil. 3월을 해의 시작으로 옮겨 윤일을 해의 끝으로 보내는 것이 요령이다.)
 */
function daysFromCivil(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year
  const era = Math.floor(y / 400)
  const yoe = y - era * 400
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy
  return era * 146097 + doe - 719468
}

/**
 * 날짜 키인가. 모양(`YYYY-MM-DD`)과 **달력**을 함께 본다 - `2026-02-30`은 모양이 맞아도 없는 날이고,
 * 없는 날이 시드가 되면 아무도 그 방을 재현할 수 없다.
 */
export function isDateKey(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_KEY_PATTERN.test(value)) return false
  const year = Number(value.slice(0, 4))
  const month = Number(value.slice(5, 7))
  const day = Number(value.slice(8, 10))
  if (month < 1 || month > 12) return false
  return day >= 1 && day <= daysInMonth(year, month)
}

/** 날짜 키 → 1970-01-01부터의 일수. 키가 아니면 `null`. */
export function dayNumber(dateKey: string): number | null {
  if (!isDateKey(dateKey)) return null
  return daysFromCivil(Number(dateKey.slice(0, 4)), Number(dateKey.slice(5, 7)), Number(dateKey.slice(8, 10)))
}

/** `to - from`(일). 둘 중 하나라도 키가 아니면 `null`. 어제 → 오늘이 1이다. */
export function daysBetween(from: string, to: string): number | null {
  const a = dayNumber(from)
  const b = dayNumber(to)
  if (a === null || b === null) return null
  return b - a
}

/* --- 시드 -------------------------------------------------------------------- */

/**
 * 그날의 방 시드. **같은 날짜 키 → 같은 방 · 같은 이상한 곳**(로드맵 M9 규칙 1)의 절반이고,
 * 나머지 절반은 `DAILY_ATTEMPT` 고정이다.
 *
 * 키가 아니면 던진다 - 조용히 아무 문자열이나 시드로 삼으면 "오늘의 방"이 사람마다 다르고, 그 사고는
 * 화면 어디에도 증상이 없다(방이 뜨긴 뜬다). 날짜 키는 `app/dateKey.ts` 한 곳에서만 오므로 여기 도달하는
 * 잘못된 값은 호출자의 버그다(`economy/pins.spendPins`와 같은 판단).
 *
 * @throws {RangeError} `dateKey`가 날짜 키가 아닐 때
 */
export function dailySeed(dateKey: string): string {
  if (!isDateKey(dateKey)) {
    throw new RangeError(`dailySeed: 날짜 키가 아니다 (받은 값: ${String(dateKey)})`)
  }
  return `${DAILY_SEED_PREFIX}${dateKey}`
}

/* --- 순수 헬퍼 ---------------------------------------------------------------- */

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** 유한한 0 이상의 수만 통과시킨다. 아니면 `null`(`progress.ts`의 같은 이름 함수와 같은 규약). */
function finiteNonNegative(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

function toNonNegative(value: unknown): number {
  return finiteNonNegative(value) ?? 0
}

function toCountOrNull(value: unknown): number | null {
  const count = finiteNonNegative(value)
  return count === null ? null : Math.floor(count)
}

/** 저장값·인자로 들어온 연속 일수를 정규화한다. 상한에서 잘린다. */
function normalizeStreak(value: unknown): number {
  const streak = finiteNonNegative(value)
  if (streak === null) return 0
  return Math.min(STREAK_CAP, Math.floor(streak))
}

/* --- 상태 -------------------------------------------------------------------- */

/** 아직 한 번도 매일의 방을 끝내지 않은 사람의 상태. 저장 파싱 실패의 폴백이기도 하다. */
export function createDailyState(): DailyState {
  return { lastDateKey: null, streak: 0, records: {} }
}

export function dailyRecordOf(state: DailyState, dateKey: string): DailyRecord | undefined {
  return state.records[dateKey]
}

/** 그날 것을 이미 끝냈는가. 화면이 "오늘 것은 끝났다"를 말하는 근거이자 "첫 결과만"의 판정식이다. */
export function hasPlayedDaily(state: DailyState, dateKey: string): boolean {
  return dailyRecordOf(state, dateKey) !== undefined
}

/**
 * 오늘까지의 연속 출석 일수. 로드맵 M9 규칙 1의 표 전부가 이 함수 하나에 있다.
 *
 *  - 마지막 출석이 없다(첫 출석) → 1
 *  - 바로 어제 → +1, `STREAK_CAP`에서 멈춘다
 *  - 같은 날 → 그대로(증가 없음)
 *  - 하루 이상 건너뜀 → 1
 *  - 지나간 날(되돌린 벽시계) → 그대로. 늘리지도 끊지도 않는다(머리말)
 */
export function streakAfter(streak: number, lastDateKey: string | null, dateKey: string): number {
  const held = normalizeStreak(streak)
  // 모르는 키로는 아무것도 움직이지 않는다. 여기서 1로 접으면 잘못된 값 하나가 출석을 만들어 낸다.
  if (!isDateKey(dateKey)) return held
  if (lastDateKey === dateKey) return Math.max(held, STREAK_START)

  const gap = lastDateKey === null ? null : daysBetween(lastDateKey, dateKey)
  if (gap === null) return STREAK_START
  if (gap === 1) return Math.min(held + 1, STREAK_CAP)
  if (gap < 0) return held
  return STREAK_START
}

/**
 * 오늘 기준으로 **살아 있는** 연속 일수. 끊겼으면 0이다.
 *
 * `state.streak`은 **마지막 출석일 기준**의 값이라 그대로 화면에 적으면 사흘 쉰 사람의 카드가 아직
 * "연속 5일"이라고 말하고, 그 판을 끝내는 순간 `streakAfter`가 1로 접는다 - 화면이 약속한 수와
 * 실제로 받는 핀이 다르다. 그 둘을 한 함수로 묶어 두는 자리가 여기다(화면은 계산하지 않는다).
 */
export function activeStreak(state: DailyState, dateKey: string): number {
  if (state.lastDateKey === null) return 0
  const gap = daysBetween(state.lastDateKey, dateKey)
  // 모르는 날짜(잘못된 키)는 판단하지 않고 저장값을 그대로 보여 준다 - 화면이 이유 없이 0을 말하지 않게.
  if (gap === null) return normalizeStreak(state.streak)
  // 하루를 건너뛰었으면 이미 끊긴 것이다. 지나간 날(gap < 0)은 아직 끊기지 않았다(머리말).
  if (gap > 1) return 0
  return normalizeStreak(state.streak)
}

/**
 * 그날의 결과를 남긴다. **첫 결과만** 남고 두 번째부터는 **참조 동일**이다(머리말).
 * 오래된 기록은 `DAILY_RECORD_KEEP`일까지만 들고 간다 - 날짜 키는 사전순이 곧 시간순이라 정렬 한 번이면 된다.
 */
export function recordDaily(state: DailyState, dateKey: string, outcome: DailyOutcome): DailyState {
  if (!isDateKey(dateKey) || hasPlayedDaily(state, dateKey)) return state

  const record: DailyRecord = {
    score: toNonNegative(outcome.score),
    foundCount: Math.floor(toNonNegative(outcome.foundCount)),
    changeCount: toCountOrNull(outcome.changeCount),
    elapsedMs: finiteNonNegative(outcome.elapsedMs),
  }

  const merged = { ...state.records, [dateKey]: record }
  return { ...state, records: prune(merged) }
}

/** 최근 `DAILY_RECORD_KEEP`일만 남긴다. 날짜 키는 사전순 = 시간순이다. */
function prune(records: Readonly<Record<string, DailyRecord>>): Readonly<Record<string, DailyRecord>> {
  const keys = Object.keys(records)
  if (keys.length <= DAILY_RECORD_KEEP) return records

  const kept = keys.sort().slice(keys.length - DAILY_RECORD_KEEP)
  const next: Record<string, DailyRecord> = {}
  for (const key of kept) {
    const record = records[key]
    if (record !== undefined) next[key] = record
  }
  return next
}

/**
 * 매일의 방 한 판을 끝냈다. 출석 갱신과 기록이 **함께** 일어나는 유일한 자리다(머리말).
 *
 * 첫 완주가 아니면(그날 것을 이미 했다 · 지나간 날 · 날짜 키가 아니다) 상태는 참조 동일이고 `first`가 거짓이다 -
 * 호출자는 그 값으로 핀을 붙일지 정한다(`economy/pins.pinsForDaily`). 판정을 호출자가 다시 하지 않게
 * 여기서 한 번에 답한다.
 */
export function finishDaily(state: DailyState, dateKey: string, outcome: DailyOutcome): DailyFinish {
  if (!isDateKey(dateKey) || hasPlayedDaily(state, dateKey)) {
    return { state, streak: state.streak, first: false, attended: false }
  }

  /*
   * 지나간 날의 판은 **기록만** 남고 출석은 전진하지 않는다(`lastDateKey`도 `streak`도 그대로다).
   *
   * 출석을 막는 이유: 벽시계를 되돌려 어제 것을 끝내 놓으면 그 뒤의 오늘이 "어제 → 오늘"이 되어
   * 연속이 한 칸 공짜로 오른다. 기록까지 막지 않는 이유는 그 반대편이 더 나쁘기 때문이다 -
   * 시계가 앞섰던 기기(또는 `?date=`로 앞날을 한 번 연 사람)는 `lastDateKey`가 미래에 머무는데,
   * 기록도 막으면 **진짜 오늘이 언제나 gap<0**이라 그날이 지나기 전까지 매일의 방이 통째로 잠긴다.
   * 화면은 그 사실을 말할 방법이 없다(카드는 계속 "오늘 것은 아직"이라고 적는다). 기능이 조용히
   * 죽는 것보다 지나간 날의 기록 한 줄이 남는 편이 낫다.
   */
  const gap = state.lastDateKey === null ? null : daysBetween(state.lastDateKey, dateKey)
  if (gap !== null && gap < 0) {
    return { state: recordDaily(state, dateKey, outcome), streak: state.streak, first: true, attended: false }
  }

  const streak = streakAfter(state.streak, state.lastDateKey, dateKey)
  const recorded = recordDaily(state, dateKey, outcome)
  return { state: { ...recorded, lastDateKey: dateKey, streak }, streak, first: true, attended: true }
}

/* --- 저장값 읽기 -------------------------------------------------------------- */

/** 저장된 기록 하나. 모양이 아니면 `null` - 그 날짜만 버리고 나머지는 살린다(`progress.parseLevelRecord`와 같은 규약). */
function parseRecord(value: unknown): DailyRecord | null {
  if (!isRecordLike(value)) return null
  return {
    score: toNonNegative(value['score']),
    foundCount: Math.floor(toNonNegative(value['foundCount'])),
    changeCount: toCountOrNull(value['changeCount']),
    elapsedMs: finiteNonNegative(value['elapsedMs']),
  }
}

/**
 * 저장값 → 매일의 방 상태. **어떤 입력에도 던지지 않는다.** 읽을 수 없으면 빈 상태다.
 *
 * 빈 상태가 새 계정과 같은 출발선이라 저장 포맷 버전을 올리지 않는다(`progress.ts` 머리말의 기준 -
 * "읽는 쪽이 채워 넣어야 하는 값인가"에 아니오다).
 */
export function parseDailyState(value: unknown): DailyState {
  if (!isRecordLike(value)) return createDailyState()

  const rawLast = value['lastDateKey']
  const lastDateKey = isDateKey(rawLast) ? rawLast : null

  const records: Record<string, DailyRecord> = {}
  const rawRecords = value['records']
  if (isRecordLike(rawRecords)) {
    for (const [key, entry] of Object.entries(rawRecords)) {
      // 날짜 키가 아닌 키는 우리가 쓴 것이 아니다(`__proto__` 같은 것도 여기서 걸린다).
      if (!isDateKey(key)) continue
      const record = parseRecord(entry)
      if (record !== null) records[key] = record
    }
  }

  /*
   * 마지막 출석이 없으면 연속도 없다. 저장을 손으로 고쳐 `streak: 7`만 남긴 상태를 그대로 믿으면
   * 다음 판에서 8일째가 되는데, 그 8일은 존재한 적이 없다.
   */
  const streak = lastDateKey === null ? 0 : Math.max(STREAK_START, normalizeStreak(value['streak']))

  return { lastDateKey, streak, records: prune(records) }
}
